import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { Entity } from '../api.js';
import { t } from '../i18n.js';
import { formatDate } from '../format.js';
import { canWriteNote, reader } from '../state.js';
import { onDownload, onEdit } from '../actions.js';
import { dismissOverlay } from '../navigation.js';
import { ObjectMenu } from './menu.js';
import { Markdown } from '../markdown.js';
import { urlFor } from '../thumbs.js';
import { Action, Empty, IconButton } from './parts.js';
import { IconClose, IconDownload, IconEdit } from './icons.js';
import { useDialog } from './dialog.js';

/**
 * **Le lecteur** — ouvrir une note ou un document.
 *
 * Trois cas, et ils ne se ressemblent pas :
 *
 * - **une note** : ses octets sont du Markdown, sous le rôle `content`, et depuis
 *   4.3 ils sont **rendus** — vers des nœuds Preact, jamais vers du HTML, ce qui
 *   rend l'injection impossible sur un contenu qu'on peut recevoir d'un groupe ;
 * - **un document lisible** (image, texte) : montré en ligne ;
 * - **le reste** : une fiche et un téléchargement.
 *
 * **Aucun `<iframe>`, aucun `<embed>`.** La CSP du site est `default-src 'none'`
 * sans `frame-src` : un PDF encadré ne s'afficherait pas, et laisserait un
 * rectangle vide sans autre indice qu'une violation en console. Le PDF se
 * télécharge — c'est moins joli, et c'est ce qui marche.
 *
 * ## Une feuille, et non une salle obscure
 *
 * Il portait la classe de la visionneuse. Le fond noir opaque est le bon réflexe
 * **pour une photo** — la couleur de la page ne doit pas déteindre sur elle —,
 * mais transposé au texte c'est un décor qui n'a rien à voir avec la lecture : on
 * lisait une note dans une boîte noire, et une note vide donnait mille pixels de
 * noir avec une phrase au milieu. Le lecteur a donc son propre décor : la teinte
 * de la page, une feuille centrée à la mesure de lecture, et la **serif** — celle
 * de la marque, parce que c'est la voix du produit.
 */
export function Reader(): JSX.Element | null {
  const entity = reader.value;
  // **La croix passe par l'historique** quand c'est le lecteur qui y est inscrit :
  // fermer au bouton sans dépiler laisserait une entrée décrivant un lecteur
  // ouvert, et le geste de retour suivant serait avalé sans rien faire de visible.
  const close = (): void => dismissOverlay('reader', () => void (reader.value = undefined));
  // Le crochet est appelé **avant** le retour anticipé : l'ordre des crochets ne
  // doit pas dépendre de l'état, et il ne fait rien tant qu'il est inactif.
  const host = useDialog(entity !== undefined, { onEscape: close, lockScroll: true });
  if (!entity) return null;

  // **Le drapeau est `size`, pas `State`** : une note créée et jamais écrite n'a
  // aucun octet sous `content`. C'est la même lecture que dans `Content`.
  const empty = entity.Variants?.['content']?.size === undefined;

  return (
    <div
      class="reader"
      ref={host}
      tabIndex={-1}
      data-testid="reader"
      role="dialog"
      aria-modal="true"
      aria-label={entity.Name}
    >
      <div class="reader-bar">
        <IconButton testId="reader-close" label={t('viewer.close')} onClick={close}>
          <IconClose />
        </IconButton>
        <span class="reader-title" data-testid="reader-title">
          {entity.Name}
        </span>
        <span class="reader-date">{formatDate(entity.DocumentDate ?? entity.CreatedAt)}</span>
        {/* Écrire n'est proposé que sur une note : un PDF n'a pas de rôle
            `content`, et `POST …/content` y rendrait `400 content_unsupported`.
            **Un seul verbe** pour ce geste — il disait « Éditer » ici et
            « Écrire » au centre de l'écran vide, pour la même destination.
            **Et un seul bouton** : sur une note vide, l'état vide porte déjà
            l'invitation, au milieu de l'écran. Deux boutons de même teinte pour
            la même destination font hésiter sur la différence entre les deux. */}
        {/* **Le mot tombe sous 52 rem** *(2026-08-15)*, comme dans la barre de
            l'éditeur : le crayon dit « écrire » dans toutes les applications, et
            la barre a besoin de sa largeur pour le titre. Le nom accessible, lui,
            ne dépend pas de la largeur. */}
        {/* **Et seulement si l'on peut écrire** *(2026-08-16)* : le bouton était
            là sur toute note, y compris reçue. Il menait à un éditeur d'où l'on
            ne ressortait plus, l'enregistrement échouant en `403` et la sortie
            passant par l'enregistrement. */}
        {entity.Type === 'NOTE' && !empty && canWriteNote(entity) ? (
          <Action
            testId="reader-edit"
            variant="primary"
            label={t('editor.write')}
            onClick={() => void onEdit(entity)}
          >
            <IconEdit size={18} />
            <span class="label-wide">{t('editor.write')}</span>
          </Action>
        ) : null}
        {/* **Le même menu d'objet qu'en grille** *(2026-08-14)*. Un objet ouvert
            n'offrait plus rien : ni détails, ni rangement en collection, ni
            suppression — il fallait le refermer, retrouver sa tuile, et viser son
            « … ». C'est le contraire de la règle du menu unique, qui veut que le
            même objet se manipule pareil partout où il se montre. */}
        {entity.Fid ? <ObjectMenu entity={entity} id={entity.Fid} opened /> : null}
      </div>

      <div class="reader-stage">
        <Content entity={entity} />
      </div>
    </div>
  );
}

function Content({ entity }: { readonly entity: Entity }): JSX.Element {
  const fid = entity.Fid ?? '';
  const role = entity.Type === 'NOTE' ? 'content' : 'source';
  const variant = entity.Variants?.[role];
  const mime = variant?.mime ?? '';
  const textual =
    entity.Type === 'NOTE' || mime.startsWith('text/') || mime === 'application/json';
  const pictorial = mime.startsWith('image/');

  const [url, setUrl] = useState<string | undefined>(undefined);
  const [text, setText] = useState<string | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  // **Le drapeau est `size`, pas `State`** : une note créée et jamais écrite n'a
  // aucun octet sous `content`, et demander son URL rendrait `409`. Le dire
  // plutôt que d'échouer est la différence entre « vide » et « cassé ».
  const hasBytes = variant?.size !== undefined;

  useEffect(() => {
    if (!hasBytes) return;
    let alive = true;
    setFailed(false);
    void urlFor(fid, role)
      .then(async (signed) => {
        if (!alive) return;
        setUrl(signed);
        if (!textual) return;
        // Le texte est relu **depuis S3**, pas depuis l'API : les octets ne
        // transitent jamais par Lambda, ici comme à l'envoi.
        const res = await fetch(signed);
        if (alive) setText(await res.text());
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [fid, role, hasBytes, textual]);

  if (!hasBytes) {
    return (
      <Empty
        action={
          entity.Type === 'NOTE' && canWriteNote(entity) ? (
            <Action testId="reader-write" variant="primary" onClick={() => void onEdit(entity)}>
              <IconEdit size={18} />
              <span>{t('editor.write')}</span>
            </Action>
          ) : undefined
        }
      >
        {entity.Type === 'NOTE' ? t('reader.emptyNote') : t('reader.noBytes')}
      </Empty>
    );
  }
  if (failed) return <Empty>{t('viewer.unavailable')}</Empty>;

  if (pictorial && url) {
    return <img class="reader-image" data-testid="reader-image" src={url} alt={entity.Name} />;
  }

  if (textual) {
    if (text === undefined) {
      return (
        <p class="empty">
          <span class="spinner" aria-hidden="true" />
        </p>
      );
    }
    // Une note est **rendue** ; un `.txt` ou un `.json` reste du texte brut, et
    // le passer au rendu Markdown inventerait des titres dans un fichier de logs.
    if (entity.Type === 'NOTE') {
      return (
        <div class="reader-text" data-testid="reader-text">
          <Markdown source={text} />
        </div>
      );
    }
    return (
      <pre class="reader-text" data-testid="reader-text">
        {text}
      </pre>
    );
  }

  return (
    <Empty
      action={
        <Action testId="reader-download" variant="primary" onClick={() => void onDownload(entity, role)}>
          <IconDownload size={18} />
          <span>{t('inspector.download')}</span>
        </Action>
      }
    >
      {t('reader.notPreviewable', { format: (variant?.ext ?? '').toUpperCase() })}
    </Empty>
  );
}
